import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const TermsOfService = () => {
  // Subtle randomized, layered gradient background (same as Home)
  const backgroundStyle = useMemo<React.CSSProperties>(() => {
    const rand = (min: number, max: number) => Math.random() * (max - min) + min;

    const hue = rand(205, 220);
    const saturation = rand(65, 78);
    const lightNearWhite = rand(96, 98);
    const lightBlue = rand(88, 92);
    const alphaSoft1 = rand(0.06, 0.1);
    const alphaSoft2 = rand(0.04, 0.08);

    const centerX = rand(30, 70);
    const centerY = rand(15, 45);

    const verticalAngle = 180;
    const horizontalAngle = 90;

    const color1 = `hsla(${hue}, ${saturation}%, ${lightNearWhite}%, 1)`;
    const color2 = `hsla(${hue}, ${saturation}%, ${lightBlue}%, 1)`;
    const tint1 = `hsla(${hue}, ${saturation}%, ${lightBlue}%, ${alphaSoft1})`;
    const tint2 = `hsla(${hue}, ${saturation}%, ${lightNearWhite}%, ${alphaSoft2})`;

    const backgroundImage = [
      `radial-gradient(ellipse at ${centerX}% ${centerY}%, ${tint1} 0%, transparent 60%)`,
      `linear-gradient(${verticalAngle}deg, #ffffff 0%, ${color2} 100%)`,
      `linear-gradient(${horizontalAngle}deg, ${tint2} 0%, transparent 50%, ${tint2} 100%)`,
    ].join(', ');

    return {
      backgroundImage,
      backgroundColor: '#ffffff',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover, cover, cover',
      backgroundAttachment: 'scroll',
    };
  }, []);

  const lastUpdated = 'November 14, 2025';

  return (
    <>
      <Helmet>
        <title>Terms of Service - Attain Domains</title>
        <meta
          name="description"
          content="Read Attain Domains Terms of Service. Understand the terms and conditions for purchasing premium domains and Lease-to-Own plans from our marketplace."
        />
      </Helmet>

      <div className="min-h-screen py-16" style={backgroundStyle}>
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <h1 className="text-6xl md:text-7xl font-bold mb-8 liquid-gradient-text drop-shadow-lg">
              Terms of Service
            </h1>
            <div className="text-3xl md:text-4xl text-foreground/70 max-w-4xl mx-auto leading-relaxed font-normal">
              Last updated: {lastUpdated}
            </div>
          </div>

          <div className="prose prose-lg max-w-none space-y-8">
            {/* 1. Introduction & Agreement */}
            <section className="bg-card rounded-lg p-6 border border-border">
              <h2 className="text-2xl font-semibold mb-4 text-foreground">
                1. Introduction & Agreement
              </h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  By accessing or using AttainDomains.com (the "Site"), you agree to be bound by
                  these Terms of Service ("Terms"). If you do not agree to these Terms, please do
                  not use the Site.
                </p>
                <p>
                  Attain Domains operates a marketplace for premium, brandable domain names for
                  professionals and businesses. We offer one-time "Buy It Now" (BIN) purchases and
                  Lease-to-Own (LTO) payment plans for many domains.
                </p>
              </div>
            </section>

            {/* 2. Eligibility & Use of the Site */}
            <section className="bg-card rounded-lg p-6 border border-border">
              <h2 className="text-2xl font-semibold mb-4 text-foreground">
                2. Eligibility & Use of the Site
              </h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  You must be able to enter into a binding contract under applicable law to use
                  Attain Domains. By using the Site, you represent that you meet this requirement.
                </p>
                <p>
                  The Site may be used for business, professional, or personal purposes, provided
                  that your use complies with these Terms and all applicable laws, regulations, and
                  third-party rights.
                </p>
              </div>
            </section>

            {/* 3. Accounts & Security */}
            <section className="bg-card rounded-lg p-6 border border-border">
              <h2 className="text-2xl font-semibold mb-4 text-foreground">3. Accounts & Security</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  If you create an account with Attain Domains, you are responsible for maintaining
                  the confidentiality of your account credentials and for all activities that occur
                  under your account.
                </p>
                <p>
                  You agree to provide accurate, current, and complete information and to update
                  such information as necessary to keep it accurate, current, and complete.
                </p>
              </div>
            </section>

            {/* 4. Domain Purchases (BIN) */}
            <section className="bg-card rounded-lg p-6 border border-border">
              <h2 className="text-2xl font-semibold mb-4 text-foreground">
                4. Domain Purchases (Buy It Now)
              </h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  <strong>Pricing:</strong> Domain prices are listed in USD and may change without
                  notice until your purchase is completed. The price displayed at the time of
                  checkout is the price you will pay.
                </p>
                <p>
                  <strong>Payment:</strong> One-time purchases are processed through Stripe. Your
                  purchase is not final until payment is successfully processed. Once payment
                  clears, Attain Domains will work with you to transfer the domain in a
                  commercially reasonable time frame, typically within 5-7 business days.
                </p>
                <p>
                  <strong>Transfer:</strong> After successful payment, Attain Domains will transfer
                  registrar ownership of the domain to you, subject to registrar and ICANN rules
                  and procedures.
                </p>
              </div>
            </section>

            {/* 5. Lease-to-Own (LTO) Plans */}
            <section className="bg-card rounded-lg p-6 border border-border">
              <h2 className="text-2xl font-semibold mb-4 text-foreground">
                5. Lease-to-Own (LTO) Plans
              </h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  <strong>How LTO Works:</strong> Lease-to-Own allows you to pay for a domain over
                  time through monthly payments. Available terms are typically 12, 24, or 36 months,
                  depending on the domain and offer.
                </p>
                <p>
                  <strong>Ownership During Lease:</strong> Until the final payment clears, Attain
                  Domains (or its registrar partner) remains the legal owner of the domain at the
                  registrar. During the lease period, you are granted DNS use of the domain,
                  meaning you can point it to your website or email services, subject to these
                  Terms and any acceptable-use rules.
                </p>
                <p>
                  <strong>Ownership Transfer:</strong> After the final payment successfully clears,
                  Attain Domains will transfer registrar ownership of the domain to you, subject to
                  registrar and ICANN rules and procedures.
                </p>
                <p>
                  <strong>Early Payoff:</strong> You may pay the remaining outstanding balance at
                  any time to own the domain sooner. Once the full balance is paid, ownership
                  transfer will proceed as described above.
                </p>
                <p>
                  <strong>Missed Payments:</strong> If payments fail or become seriously delinquent,
                  Attain Domains may suspend DNS use of the domain. After a reasonable grace
                  period, Attain Domains may terminate the lease and return the domain to inventory.
                  If the lease is terminated before the final payment, you will not obtain ownership
                  of the domain.
                </p>
              </div>
            </section>

            {/* 6. Pricing, Payments & Taxes */}
            <section className="bg-card rounded-lg p-6 border border-border">
              <h2 className="text-2xl font-semibold mb-4 text-foreground">
                6. Pricing, Payments & Taxes
              </h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  <strong>Payment Processing:</strong> All online payments for BIN purchases and
                  LTO plans are processed through Stripe. Stripe Invoicing may be used for
                  consulting add-ons or custom arrangements.
                </p>
                <p>
                  <strong>Price Changes:</strong> Prices and payment terms may change over time.
                  Changes do not retroactively affect completed purchases or existing LTO contracts
                  unless explicitly agreed upon by both parties.
                </p>
                <p>
                  <strong>Taxes:</strong> You are responsible for any applicable taxes, fees, or
                  duties associated with your purchase. Attain Domains will collect sales tax where
                  required by law.
                </p>
              </div>
            </section>

            {/* 7. Refunds, Cancellations & Chargebacks */}
            <section className="bg-card rounded-lg p-6 border border-border">
              <h2 className="text-2xl font-semibold mb-4 text-foreground">
                7. Refunds, Cancellations & Chargebacks
              </h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  <strong>Domain Transactions Are Generally Final:</strong> Once a domain is
                  transferred to you (for BIN purchases) or once DNS access is granted (for LTO
                  plans), transactions are typically final and non-refundable. Domain transactions
                  are unique digital assets, and transfers cannot be easily reversed.
                </p>
                <p>
                  <strong>Chargebacks:</strong> Misuse of chargebacks or payment disputes may result
                  in suspension or termination of your account. If you have concerns about a
                  transaction, please contact us directly at{' '}
                  <Link to="/contact" className="text-primary hover:underline">
                    our contact page
                  </Link>{' '}
                  before initiating a chargeback.
                </p>
              </div>
            </section>

            {/* 8. Acceptable Use & Prohibited Activities */}
            <section className="bg-card rounded-lg p-6 border border-border">
              <h2 className="text-2xl font-semibold mb-4 text-foreground">
                8. Acceptable Use & Prohibited Activities
              </h2>
              <div className="space-y-3 text-muted-foreground">
                <p>You agree not to use any domain obtained through Attain Domains for:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Illegal, abusive, or fraudulent activities</li>
                  <li>Infringing on third-party intellectual property rights</li>
                  <li>Spam, phishing, or other deceptive practices</li>
                  <li>Any activity that violates applicable laws or regulations</li>
                </ul>
                <p>
                  Attain Domains reserves the right to suspend DNS use or services if domains are
                  used in ways that violate laws or these Terms.
                </p>
              </div>
            </section>

            {/* 9. Intellectual Property */}
            <section className="bg-card rounded-lg p-6 border border-border">
              <h2 className="text-2xl font-semibold mb-4 text-foreground">
                9. Intellectual Property
              </h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  <strong>Attain Domains' Rights:</strong> Attain Domains retains all rights in its
                  own trademarks, brand assets, website content, and software. Your use of a
                  domain purchased through Attain Domains does not grant you any rights in the
                  Attain Domains brand or intellectual property.
                </p>
                <p>
                  <strong>Domain Ownership:</strong> Domains purchased through BIN or fully
                  paid-off under LTO become your asset at the registrar, subject to registrar and
                  ICANN rules. You are responsible for ensuring your use of the domain does not
                  infringe on third-party rights.
                </p>
              </div>
            </section>

            {/* 10. Disclaimers & Limitation of Liability */}
            <section className="bg-card rounded-lg p-6 border border-border">
              <h2 className="text-2xl font-semibold mb-4 text-foreground">
                10. Disclaimers & Limitation of Liability
              </h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  <strong>No Warranties:</strong> Attain Domains provides domains "as is" and "as
                  available." We do not guarantee that any domain will produce particular business
                  results, SEO value, traffic, or search rankings. We provide clean title and
                  unlocked domains ready for transfer, but make no other warranties.
                </p>
                <p>
                  <strong>Limitation of Liability:</strong> To the maximum extent permitted by law,
                  Attain Domains' total liability is limited to the amount you paid for the
                  affected domain (or 12 months of lease payments, whichever is less). Attain
                  Domains shall not be liable for any indirect, incidental, special,
                  consequential, or punitive damages, including loss of profits, data, use,
                  goodwill, or other intangible losses.
                </p>
              </div>
            </section>

            {/* 11. Changes to the Terms */}
            <section className="bg-card rounded-lg p-6 border border-border">
              <h2 className="text-2xl font-semibold mb-4 text-foreground">
                11. Changes to the Terms
              </h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  Attain Domains may update these Terms from time to time. We will notify users of
                  material changes by updating the "Last updated" date at the top of this page and
                  posting a notice on the Site. Material changes may also be announced via email if
                  you have an account with us.
                </p>
                <p>
                  Your continued use of Attain Domains after changes are posted constitutes
                  acceptance of the updated Terms. We encourage you to review these Terms
                  periodically.
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default TermsOfService;
